// R-131-FIN-DECISION-K — deterministic pathway comparison section for reports.

import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import type { PathwayClassification } from '../../../../lib/finDecision/types';
import {
  formatUsdCompactMillions,
  formatUsdPerMwh,
} from '../../../../lib/finDecision/pathwayCompareMetrics';
import { buildPathwayComparisonFootnote } from '../../../../lib/reportGeneration/evidence/buildPathwayComparisonEvidence';
import { getBtmDecisionState, type BtmDecisionState } from '../../../../lib/btmDecision';
import { SectionShell } from '../SectionShell';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import {
  EditorialCardGrid,
  EditorialCallout,
  type EditorialCard,
} from '../../report/editorial';
import { ReportSourceNote } from '../../report/ReportSourceNote';

const MONO = 'var(--report-body-font, inherit)';

/**
 * Task 3 — Studio-preview signal.
 *
 * The live viewer and the PDF/DOCX export must render NOTHING (reserve no
 * page) for an optional section with no real data. The Template Studio
 * preview, in contrast, must KEEP the empty placeholder so a template author
 * can see + position the section before any data exists.
 *
 * `viewMode==='export'` does NOT distinguish these two: the Studio preview
 * also renders in export mode. The only authoritative signal is the
 * `studioPreview` prop on SiteRagReportView. This context lets that flag reach
 * BOTH the inline pathway-comparison mount (feasibility / SiteRagReportView)
 * and the router-driven mount (financial / test-fit / executive-summary via
 * NewTypeSectionsRouter) WITHOUT threading a prop through every intermediate
 * component. Default `false` (no provider) ⇒ live/export behaviour: an empty
 * optional section self-omits.
 */
const StudioPreviewContext = React.createContext<boolean>(false);

/** Wrap the report tree so deep section components can read the studio-preview flag. */
export function StudioPreviewProvider({
  value,
  children,
}: {
  value: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return <StudioPreviewContext.Provider value={value}>{children}</StudioPreviewContext.Provider>;
}

/** True only when the tree is rendering inside the Template Studio preview. */
export function useStudioPreview(): boolean {
  return React.useContext(StudioPreviewContext);
}

/** Reader-facing classification label for the card eyebrow / Type. */
function classificationLabel(c: PathwayClassification | undefined): string {
  if (c === 'grid') return 'Grid';
  if (c === 'btm') return 'BTM';
  if (c === 'hybrid') return 'Hybrid';
  return 'Pathway';
}

/**
 * Map the report's REAL BTM decision state to the pathway classification the
 * 16B card grid should ring as recommended. `decision-pending` (and any state
 * with no classification, e.g. a future scorer) returns null so NO card is
 * ringed — we never fabricate a winner.
 */
function recommendedClassificationFor(
  state: BtmDecisionState,
): PathwayClassification | null {
  switch (state) {
    case 'btm-recommended':
      return 'btm';
    case 'grid-recommended':
      return 'grid';
    case 'hybrid-candidate':
      return 'hybrid';
    default:
      return null;
  }
}

export const PathwayComparisonReportSection: React.FC<{
  report: SiteRagReport;
  label: string;
  /**
   * Skip the inner SectionShell heading when this component is mounted
   * inside an outer EditorialSectionShell that already renders the
   * section h2 (the feasibility "pinned pathway comparison" inline
   * mount in SiteRagReportView). Without this, the section's title
   * gets rendered twice (and indented), which is what users see as
   * "Pathway Comparison is not left-aligned with neighbouring sections".
   */
  suppressHeading?: boolean;
}> = ({ report, label, suppressHeading }) => {
  const theme = useEffectiveReportTheme();
  const studioPreview = useStudioPreview();
  const snapshot = report.pathwayComparisonAtGen;
  const hasData = Boolean(snapshot && snapshot.pathways.length >= 2);

  // Task 3 — in the live viewer and the PDF/DOCX export, an unpinned pathway
  // comparison (no snapshot with 2+ alternatives) must render NOTHING so it
  // reserves no page. The Template Studio preview KEEPS the placeholder (so a
  // template author can see + place the section), so the self-omit is gated on
  // `!studioPreview`. When data IS present the section renders identically in
  // all three surfaces.
  if (!studioPreview && !hasData) return null;

  // Domain-accurate empty message: the missing input is a PINNED pathway
  // comparison on the site (report.pathwayComparisonAtGen with 2+ alternatives),
  // NOT a SED field — so do not emit SectionShell's generic "requires inputs not
  // surfaced in the SED" copy, which mis-attributes the gap.
  const emptyMessage =
    'Pin a pathway comparison (2+ alternatives) on the site to populate this section.';

  return (
    <SectionShell
      label={label}
      hasData={hasData}
      suppressHeading={suppressHeading}
      emptyMessage={emptyMessage}
    >
      {snapshot && hasData ? (
        (() => {
          // PATHWAY 16B (BATCH R2) — locked editorial pathway cards (was a raw
          // token-driven <table>). Each pathway → one EditorialCardGrid card:
          //   eyebrow = classification (Grid / BTM / Hybrid),
          //   title   = pathway name,
          //   rows    = the REAL snapshot metrics (Headline / LCOE / NPV).
          //
          // GAP (KNOWN, gen-side): the per-pathway NUMERIC SCORE (58 / 79 / 88)
          // is NOT generated, so `card.score` is intentionally OMITTED — we
          // never fabricate a score. The recommended pathway is RINGED from the
          // report's REAL BTM decision state (getBtmDecisionState → a pathway
          // classification); we ring a card only when EXACTLY ONE pathway carries
          // the recommended classification (no ambiguous / fabricated winner).
          const recommendedClass = recommendedClassificationFor(getBtmDecisionState(report));
          const matchingCount = recommendedClass
            ? snapshot.pathways.filter((p) => p.classification === recommendedClass).length
            : 0;
          const ringOne = recommendedClass != null && matchingCount === 1;

          const cards: EditorialCard[] = snapshot.pathways.map((row) => ({
            eyebrow: classificationLabel(row.classification),
            title: row.name,
            // GAP: no per-pathway numeric score generated — score omitted.
            recommended: ringOne && row.classification === recommendedClass,
            rows: [
              { label: 'Headline', value: row.headlineLabel },
              { label: 'LCOE', value: formatUsdPerMwh(row.lcoeUsdPerMwh) },
              { label: 'NPV', value: formatUsdCompactMillions(row.npvUsd) },
            ],
          }));

          return (
            <div className="space-y-4">
              {/* Recommendation — editorial accent callout (was two raw <p>
                  lead lines). decisionRecommendation is the headline; the
                  primary reason (when present) is the body. */}
              <EditorialCallout
                theme={theme}
                label="Recommendation"
                pagedSplitAfter
              >
                <span style={{ fontWeight: 500, color: roleColor(theme, 'ink') }}>
                  {snapshot.decisionRecommendation}
                </span>
                {snapshot.recommendationPrimaryReason ? (
                  <span style={{ display: 'block', marginTop: 4 }}>
                    {snapshot.recommendationPrimaryReason}
                  </span>
                ) : null}
              </EditorialCallout>

              {/* data-span-columns: defensive — lets the wide pathway cards span
                  both flow columns if supportsColumnLayout is enabled. */}
              <div data-span-columns data-paged-split-suffix="true">
                <EditorialCardGrid theme={theme} cards={cards} />
              </div>

              <ReportSourceNote style={{ fontFamily: MONO }}>
                {buildPathwayComparisonFootnote({
                  comparisonId: snapshot.comparisonId,
                  name: snapshot.name,
                  updatedAt: snapshot.updatedAt,
                  citationSourceId: `pathwayComparison:${snapshot.comparisonId}`,
                  viewPathwaysUrl: snapshot.viewPathwaysUrl,
                  decisionRecommendation: snapshot.decisionRecommendation,
                  pathways: snapshot.pathways,
                  atGen: snapshot,
                })}
              </ReportSourceNote>
            </div>
          );
        })()
      ) : null}
    </SectionShell>
  );
};
